import React, { useState, useEffect } from 'react';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import MetricsStrip from './components/MetricsStrip';
import AcquisitionFunnel from './components/AcquisitionFunnel';
import EngagementLeaderboard from './components/EngagementLeaderboard';
import AttendanceHeatmap from './components/AttendanceHeatmap';
import RenewalOpportunities from './components/RenewalOpportunities';
import CohortAnalysis from './components/CohortAnalysis';
import ChurnPrediction from './components/ChurnPrediction';
import MemberDataGrid from './components/MemberDataGrid';
import FilterBar from './components/FilterBar';
import api from '../../lib/api';

const TABS = [
  { id: 'acquisition', label: 'Acquisition', icon: 'UserPlus' },
  { id: 'retention', label: 'Retention', icon: 'RefreshCw' },
  { id: 'engagement', label: 'Engagement', icon: 'Activity' },
  { id: 'members', label: 'Members', icon: 'Users' },
];

const MemberAnalyticsHub = () => {
  const [activeTab, setActiveTab] = useState('acquisition');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [metrics, setMetrics] = useState([
    { id: 1, label: "Member Acquisition", value: "...", subLabel: "New members this month", change: "0%", trend: "up", icon: "UserPlus", color: "#0F766E", progress: 0 },
    { id: 2, label: "Retention Rate", value: "...", subLabel: "30-day cohort average", change: "0%", trend: "up", icon: "RefreshCw", color: "#0D9488", progress: 0 },
    { id: 3, label: "Avg. Lifetime Value", value: "...", subLabel: "Per active member", change: "0%", trend: "up", icon: "DollarSign", color: "#7C3AED", progress: 0 },
    { id: 4, label: "Churn Alerts", value: "...", subLabel: "Members at high risk", change: "0 new", trend: "down", icon: "AlertTriangle", color: "#DC2626", progress: 0 },
  ]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  const fetchMetrics = async () => {
    try {
      setLoadingMetrics(true);

      const [membersRes, paymentsRes] = await Promise.all([
        api.get('/members', { params: { limit: 2000 } }),
        api.get('/payments', { params: { limit: 2000 } }),
      ]);

      const allMembers = membersRes?.data ?? [];
      const allPayments = paymentsRes?.data ?? [];

      const total = allMembers?.length;

      const startOfMonth = new Date();
      startOfMonth?.setDate(1);
      startOfMonth?.setHours(0, 0, 0, 0);
      const startOfMonthStr = startOfMonth?.toISOString()?.split('T')?.[0];

      const newThisMonth = allMembers?.filter(m => m?.join_date >= startOfMonthStr)?.length;
      const activeCount = allMembers?.filter(m => m?.member_status === 'active')?.length;
      const inactiveCount = allMembers?.filter(m => m?.member_status === 'inactive')?.length;

      const paidPayments = allPayments?.filter(p => p?.payment_status === 'paid');
      const totalRevenue = paidPayments?.reduce((sum, p) => sum + (parseFloat(p?.paid_amount) || 0), 0) || 0;
      const uniquePayingMembers = new Set(paidPayments?.map(p => p?.member_id))?.size || 1;
      const avgLTV = totalRevenue / Math.max(uniquePayingMembers, 1);

      const retentionRate = total > 0 ? ((activeCount || 0) / total * 100)?.toFixed(1) : 0;

      const in30Days = new Date();
      in30Days?.setDate(in30Days?.getDate() + 30);
      const todayStr = new Date()?.toISOString()?.split('T')?.[0];
      const in30DaysStr = in30Days?.toISOString()?.split('T')?.[0];
      const expiringCount = allMembers?.filter(m => m?.package_end_date && m?.package_end_date >= todayStr && m?.package_end_date <= in30DaysStr)?.length;

      setTotalMembers(total);
      setLastUpdated(new Date());

      const acquisitionProgress = Math.min(Math.round(((newThisMonth || 0) / Math.max(total, 1)) * 100 * 5), 100);

      setMetrics([
        {
          id: 1,
          label: "Member Acquisition",
          value: String(newThisMonth || 0),
          subLabel: "New members this month",
          change: `${newThisMonth || 0} new`,
          trend: (newThisMonth || 0) > 0 ? "up" : "neutral",
          icon: "UserPlus",
          color: "#0F766E",
          progress: acquisitionProgress,
        },
        {
          id: 2,
          label: "Retention Rate",
          value: `${retentionRate}%`,
          subLabel: "Active vs total members",
          change: `${activeCount || 0} active`,
          trend: parseFloat(retentionRate) >= 70 ? "up" : "down",
          icon: "RefreshCw",
          color: "#0D9488",
          progress: parseFloat(retentionRate),
        },
        {
          id: 3,
          label: "Avg. Lifetime Value",
          value: `$${avgLTV?.toFixed(0)}`,
          subLabel: "Per paying member",
          change: `$${totalRevenue?.toFixed(0)} total`,
          trend: avgLTV > 0 ? "up" : "neutral",
          icon: "DollarSign",
          color: "#7C3AED",
          progress: Math.min(Math.round(avgLTV / 10), 100),
        },
        {
          id: 4,
          label: "Churn Alerts",
          value: String(expiringCount || 0),
          subLabel: "Packages expiring in 30 days",
          change: `${inactiveCount || 0} inactive`,
          trend: (expiringCount || 0) > 5 ? "down" : "neutral",
          icon: "AlertTriangle",
          color: "#DC2626",
          progress: Math.min(Math.round(((expiringCount || 0) / Math.max(total, 1)) * 100 * 5), 100),
        },
      ]);
    } catch (err) {
      console.error('Error fetching analytics metrics:', err);
    } finally {
      setLoadingMetrics(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleFilterChange = (filters) => {
    console.log('Filters changed:', filters);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold font-heading text-[var(--color-foreground)]">
              Member Analytics Hub
            </h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-caption mt-1">
              Lifecycle tracking, engagement patterns &amp; retention intelligence
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)] font-caption">
              <div className="w-2 h-2 rounded-full bg-[var(--color-success)] animate-pulse" />
              <span>Live · Updated {formatTime(lastUpdated)}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg">
              <Icon name="Users" size={14} color="var(--color-primary)" />
              <span className="text-xs font-medium font-caption text-[var(--color-foreground)]">
                {loadingMetrics ? '...' : totalMembers} Total Members
              </span>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <FilterBar onFilterChange={handleFilterChange} />

        {/* Metrics Strip */}
        <MetricsStrip metrics={metrics} />

        {/* Mobile Tab Navigation */}
        <div className="lg:hidden mb-6 overflow-x-auto">
          <div className="flex gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-1 min-w-max">
            {TABS?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-caption font-medium transition-all whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
              >
                <Icon
                  name={tab?.icon}
                  size={15}
                  color={activeTab === tab?.id ? '#FFFFFF' : 'currentColor'}
                />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: Full Layout | Mobile: Tabbed */}
        <div className="hidden lg:block space-y-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-8">
              <AcquisitionFunnel />
            </div>
            <div className="col-span-12 xl:col-span-4 space-y-5">
              <EngagementLeaderboard />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-8">
              <CohortAnalysis />
            </div>
            <div className="col-span-12 xl:col-span-4 space-y-5">
              <AttendanceHeatmap />
              <RenewalOpportunities />
            </div>
          </div>
          <ChurnPrediction />
          <MemberDataGrid />
        </div>

        {/* Mobile Tabbed Content */}
        <div className="lg:hidden space-y-5">
          {activeTab === 'acquisition' && (
            <>
              <AcquisitionFunnel />
              <CohortAnalysis />
            </>
          )}
          {activeTab === 'retention' && (
            <>
              <ChurnPrediction />
              <RenewalOpportunities />
            </>
          )}
          {activeTab === 'engagement' && (
            <>
              <EngagementLeaderboard />
              <AttendanceHeatmap />
            </>
          )}
          {activeTab === 'members' && (
            <MemberDataGrid />
          )}
        </div>
      </main>
    </div>
  );
};

export default MemberAnalyticsHub;